/**
 * Editorial content for /about.
 *
 * The About page is a long document, and mixing its prose into the section
 * components buries the layout under data. Keeping the copy here lets each
 * section file read as an outline, and it makes the claims easy to audit in one
 * place when the product changes.
 *
 * The model lineup is deliberately not written here: it is generated from the
 * seed file that defines what actually ships, and re-exported so callers have
 * one import for the page's content.
 */
import {
  MODALITY_COUNTS,
  MODEL_PROVIDERS,
  MODEL_TOTAL,
  PROVIDER_TOTAL,
} from './modelInventory.generated';

export { MODALITY_COUNTS, MODEL_PROVIDERS, MODEL_TOTAL, PROVIDER_TOTAL };

/** Mediums the product covers, shown as a rail under the masthead. */
export const CAPABILITIES = [
  { icon: 'pen', label: 'Text' },
  { icon: 'image', label: 'Image' },
  { icon: 'video', label: 'Video' },
  { icon: 'music', label: 'Music' },
  { icon: 'cube', label: '3D' },
  { icon: 'code', label: 'Code' },
  { icon: 'microphone', label: 'Voice' },
  { icon: 'globe', label: 'Web search' },
  { icon: 'folder-open', label: 'Files' },
];

export const HERO_MARQUEE = [
  'Founded 2016', 'Made in USA', 'Privacy first', 'Every major model',
  'One subscription', 'Free speech', 'Christ is King',
];

/** Company story and the principles expressed in the product. */
export const STORY_PARAGRAPHS = [
  'We started Gab AI with a conviction: AI should help people become more capable. It should leave room for independent thought, respect the people using it, and give them control over their work.',
  'That conviction shapes what we build. We bring leading models into one place and give them the tools to do useful work: research a subject, make a film, prepare a presentation, or turn an idea into working software. Our measure of progress is what you can accomplish with it.',
];

export const ABOUT_PRINCIPLES = [
  {
    title: 'Free expression',
    copy: 'We believe in the First Amendment and the freedom to ask questions, explore ideas, and think for yourself. Those beliefs are part of why Gab AI exists.',
  },
  {
    title: 'Control over your data',
    copy: 'We don’t sell your personal information or use your conversations to train our models. You can manage your chat history and choose what your AI remembers, including editing or deleting memories.',
    href: '/privacy-policy',
    link: 'Read our privacy policy',
  },
  {
    title: 'The freedom to choose',
    copy: 'Different models have different strengths. Choose the one you want to use, keep your work together, and take your files with you in familiar formats.',
  },
];

export const STORY_CLOSING = `We’re an American company. We build for everyone. And we mean it when we say your data is yours.`;

export const STORY_FIGURES = [
  { value: String(MODEL_TOTAL), label: 'Live models in one chat' },
  { value: String(PROVIDER_TOTAL), label: 'Providers under one account' },
  { value: '50+', label: 'Specialized creative tools' },
  { value: '2016', label: 'Founded in Pennsylvania' },
];

export const PRIVACY_PILLARS = [
  {
    icon: 'user-secret',
    title: 'Anonymous by design',
    copy: 'When we route requests to third-party models, your identity is masked. Requests are batched and anonymized — no one can trace responses back to you.',
  },
  {
    icon: 'ban',
    title: 'Never trained on your data',
    copy: 'Your conversations are never used to train our models. Period. What you say stays between you and your AI — it’s never fed back into the system.',
  },
  {
    icon: 'clock',
    title: 'Temporary chats',
    copy: 'Need extra privacy? Temporary chat mode never saves your conversation — it disappears the moment you close the chat.',
  },
];

export const PRIVACY_PROMISES = [
  'No data selling',
  'No targeted ads',
  'Delete anytime',
  'You own your data',
];

export const ENGINE_STEPS = [
  {
    icon: 'bolt',
    title: 'Reads your intent',
    copy: 'Whether you ask for an image, a song, a fact-check, a refactor, or a research brief, the engine picks up the type of work in milliseconds.',
  },
  {
    icon: 'arrows-rotate',
    title: 'Routes to the best model',
    copy: 'Each request goes to whichever model is currently best for that job. New leaders ship; we wire them in. You don’t change a thing.',
  },
  {
    icon: 'wand-magic-sparkles',
    title: 'Returns one clean answer',
    copy: 'No tab switching, no re-prompting, no five subscriptions. The result lands inline in the same conversation you started in.',
  },
];

export const WORKBENCH = [
  {
    icon: 'magnifying-glass',
    title: 'Real-time AI search',
    copy: 'Ask about breaking news, products, companies, court cases, markets, or anything else that changes by the hour. Gab AI searches the web, reads sources, and turns the results into a clear answer.',
    points: [
      { icon: 'globe', label: 'Current web results inside chat' },
      { icon: 'newspaper', label: 'News, research, products, and market lookups' },
      { icon: 'quote-left', label: 'Source-aware summaries and follow-up questions' },
    ],
  },
  {
    icon: 'folder-open',
    title: 'File management built in',
    copy: 'Upload your PDFs, documents, images, audio, spreadsheets, and project files. Keep them organized, reuse them across chats, and ask AI to summarize, compare, extract, translate, or create from them.',
    points: [
      { icon: 'paperclip', label: 'Upload and organize files once' },
      { icon: 'file-lines', label: 'Analyze documents, images, and transcripts' },
      { icon: 'layer-group', label: 'Connect files to chats, tools, and projects' },
    ],
  },
];

export const TOOL_CATEGORIES = [
  {
    category: 'Create',
    icon: 'sparkles',
    tools: ['AI Image Generator', 'AI Video Generator', 'AI Music Generator', 'AI 3D Generator', 'AI Text-to-Speech', 'AI Code Generator'],
  },
  {
    category: 'Edit',
    icon: 'wand-magic-sparkles',
    tools: ['AI Image Editor', 'AI Object Remover', 'AI Image Upscaler', 'AI Background Remover', 'AI Photo Restorer', 'AI Style Transfer'],
  },
  {
    category: 'Write',
    icon: 'pen-nib',
    tools: ['AI Blog Post Writer', 'AI Copywriter', 'AI Email Writer', 'AI Translator', 'AI Summarizer', 'AI Lyrics Writer'],
  },
  {
    category: 'Analyze',
    icon: 'magnifying-glass-chart',
    tools: ['AI Research Assistant', 'AI Fact Checker', 'AI Content Detector', 'AI YouTube Summarizer', 'AI Document Q&A', 'AI Model Comparison'],
  },
  {
    category: 'Code',
    icon: 'code',
    tools: ['AI Code Reviewer', 'AI Code Explainer', 'AI SQL Builder', 'AI Regex Builder', 'AI Bug Finder', 'AI Test Writer'],
  },
  {
    category: 'Business',
    icon: 'briefcase',
    tools: ['AI SEO Writer', 'AI Marketing Copy', 'AI Social Media Posts', 'AI YouTube Script', 'AI Cold Email', 'AI Press Release'],
  },
];

export const VOICE_PROMPTS = [
  'Help me think through this idea…',
  'Practice this interview with me…',
  'What’s in the news today?',
];

/** Orbit labels for the voice visual. Angles are degrees clockwise from top. */
export const VOICE_ORBIT = [
  { label: 'Brainstorm', angle: -60 },
  { label: 'Practice', angle: 0 },
  { label: 'Dictation', angle: 60 },
  { label: 'Search', angle: 120 },
  { label: 'Chat', angle: 180 },
  { label: 'Files', angle: -120 },
];

export const MEMORY_STEPS = [
  {
    title: 'Learns as you chat',
    copy: 'Say "remember this" and Gab AI saves it instantly. It also reviews recent conversations in the background and keeps a living library of durable facts about you.',
  },
  {
    title: 'Add your own',
    copy: 'Want it to know your job title, your writing voice, the names of your kids? Add memories manually any time.',
  },
  {
    title: 'Watch it get sharper',
    copy: 'Your AI starts anticipating instead of asking. It speaks your language. The deltas compound the longer you stay.',
  },
];

export const MEMORY_CONTROLS = [
  'View everything your AI has learned',
  'Edit or delete any memory anytime',
  'Turn off learning or memory entirely with one click',
  'Memories are never shared, sold, or used to train',
];

export const SCHEDULED_TASKS = [
  {
    icon: 'newspaper',
    title: 'Daily news briefing',
    copy: 'Wake up to a personalized summary of the news that matters to you. Topics you choose, written in the tone you want.',
    schedule: 'Daily · 7:00 AM',
  },
  {
    icon: 'microchip',
    title: 'Tech and AI updates',
    copy: 'Stay ahead with daily updates on AI breakthroughs, software releases, and tech industry news tailored to your interests.',
    schedule: 'Daily · 8:00 AM',
  },
  {
    icon: 'chart-line',
    title: 'Market summary',
    copy: 'End your day with a comprehensive market analysis covering the sectors you actually care about.',
    schedule: 'Daily · 5:00 PM',
  },
];

export const COMPARE_PROMPT = 'Explain quantum computing in simple terms';

export const COMPARE_ANSWERS = [
  {
    name: 'GPT-5',
    response: 'Think of quantum computing like a maze. A regular computer tries one path at a time. A quantum computer explores all paths simultaneously…',
  },
  {
    name: 'Claude',
    response: 'Imagine flipping a coin. Normally it’s heads or tails. In quantum computing, the coin is both heads AND tails until you look…',
  },
  {
    name: 'Gemini',
    response: 'Picture a library where a regular computer reads one book at a time. A quantum computer reads every book in the library at once…',
  },
];

export const PLATFORMS = [
  { icon: 'mobile-screen', label: 'iOS & iPadOS' },
  { icon: 'mobile-screen-button', label: 'Android' },
  { icon: 'desktop', label: 'macOS' },
  { icon: 'globe', label: 'Web' },
];

/** Source for the developer section's dark card. Coloured at render time. */
export const API_SNIPPET = `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_GAB_API_KEY",
    base_url="https://gab.ai/v1",
)

response = client.chat.completions.create(
    model="auto",
    messages=[
        {"role": "user", "content": "Explain the routing engine"}
    ],
)

print(response.choices[0].message.content)`;

export const API_CAPABILITIES = [
  { icon: 'route', label: 'Auto model routing with model="auto"' },
  { icon: 'code-branch', label: 'OpenAI-compatible routes when you need them' },
  { icon: 'box', label: 'Native SDKs for Python and Node.js' },
  { icon: 'bolt', label: 'Streaming responses for real-time UX' },
  { icon: 'image', label: 'Text, image, video, and audio outputs' },
];

export const DIFFERENTIATORS = [
  {
    icon: 'layer-group',
    title: 'All models, one spot',
    copy: 'GPT, Claude, Gemini, DeepSeek, Flux, Sora, Veo, Kling, ElevenLabs — already wired in.',
  },
  {
    icon: 'graduation-cap',
    title: 'Memory that gets sharper',
    copy: 'Train your AI on your style and preferences. You decide what it remembers — and what it forgets.',
  },
  {
    icon: 'shield-halved',
    title: 'Privacy-first, no exceptions',
    copy: 'We mask your identity, never train on your data, and never sell your information.',
  },
  {
    icon: 'flag-usa',
    title: 'Made in USA',
    copy: 'Built in Pennsylvania. Owned by no one but us. Free speech is a feature, not an afterthought.',
  },
];

export const CLOSING_MARKS = ['Christ is King', 'Made in USA', 'Founded 2016'];
